<template>

  <div class="ts-image" :class="[{dark: darkLoader}]" :style="style">

    <!-- image to be loaded in -->
    <img v-show="loaded" :src="src">

    <div v-show="!loaded" class="preloader" slot="placeholder">
      <!-- replace this with something else -->
      <div class="square square-c state1c"></div><div class="square square-c state2c"></div>
    </div>
  </div>

</template>


<script>

import sal from 'sal.js'

export default {

  name: 'TsImage',

  props: {
    bgColor: {
      type: String,
      default: 'none',
    },
    darkLoader: {
      type: Boolean,
      default: false
    },
    height: {
      type: Number,
      default: null,
    },
    width: {
      type: Number,
      default: null,
    },
    src: {
      type: String,
      required: true
    },
    element: String,
    threshold: {
      type: [Array, Number],
      default: () => {
        return [0, 0.5, 1]
      }
    },
    ratio: {
      type: Number,
      default: 0.4,
      validator(value) {
        // can't be less or equal to 0 and greater than 1
        return value > 0 && value <= 1
      }
    },
    margin: {
      type: String,
      default: (window.innerHeight * 2) + "px"
    },
    crossorigin: {
      type: String,
      default: null,
      validator(value) {
        return value === 'anonymous' || value === 'use-credentials'
      }
    },
    loadedClass: {
      type: String,
      default: ' loaded'
    },
    loadingClass: {
      type: String,
      default: ' loading'
    },
    errorClass: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loaded: false,
      observer: null,
      errored: false
    }
  },
  computed: {
    aspectRatio() {
      // Calculate the aspect ratio of the image
      // if the width and the height are given.
      if (!this.width || !this.height) return null;
      return (this.height / this.width) * 100;
    },
    style() {

      let style = { backgroundColor: this.bgColor };
      const applyAspectRatio = !this.loaded && this.aspectRatio;

      if (applyAspectRatio) {
        // Prevent flash of unstyled image after the image is loaded.
        style.height = 0;
        // Scale the image container according to the aspect ratio.
        style.paddingTop = `${this.aspectRatio}%`;
      }

      if (this.loaded) return { backgroundColor: "none" };
      return style;
    },
  },

  methods: {

    load() {
      // emits 'loading' event upwards
      this.$emit('loading')

      // don't load more than once
      this.observer.disconnect()

      if (!this.loaded) {
        // fake image
        let img = new Image
        // solid 1 pixel as placeholder
        img.addEventListener('load', () => {
          this.loaded = true
          // emits 'load' event upwards
          this.$emit('load')
          clear()
        })

        img.addEventListener('error', (event) => {
          this.errored = true
          // emits 'error' event upwards
          // adds the original event as argument
          this.$emit('error', event)
          clear()
        })

        // function used to clear variables from memory
        const clear = () => {
          // discard fake image
          img = null
          // remove observer from memory
          this.observer = null
        }

        // CORS mode configuration
        if (this.crossorigin !== null) {
          img.crossOrigin = this.crossorigin
        }

        img.src = this.src

      }
    },

    /**
     * Creates IntersectionObserver instance and observe current element
     */
    observe() {
      const options = {
        threshold: this.threshold,
        root: this.element ? document.querySelector(this.element) : null,
        rootMargin: this.margin
      }

      // creates IO instance
      this.observer = new IntersectionObserver((entries) => {
        // as we instantiated one for each component
        // we can directly access the first index
        if (entries[0].intersectionRatio >= this.ratio) {
          this.load()
        }
      }, options)

      // start observing main component
      this.observer.observe(this.$el)
    }
  },

  mounted() {
    // start observing the element visibility
    sal({threshold: 0.25})
    this.$nextTick(this.observe)
  }

}

</script>
